// Wrapper canónico de plan-gating (show-with-lock). `FeatureLock` delega acá.
// Regla: una feature gateada se MUESTRA con candado + plan requerido + CTA a
// billing, nunca se oculta ni se deja disabled sin explicación.
//
//   <FeatureGate product="360" feature="white_label" tour={tour}>…</FeatureGate>
import {
  type GateProduct,
  type PlanFeature,
  planDisplayLabel,
} from "@u/featureGating";
import { type GateResource, useFeatureGate } from "@h/useFeatureGate";
import { cn } from "@u/cn";
import { buildRoute } from "@u/routes";
import Button from "@ui/button";
import { Check, CreditCard, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export type FeatureGateVariant = "overlay" | "badge" | "wall";

interface CommonProps {
  variant?: FeatureGateVariant;
  /** overlay: blur sutil que deja ver el contenido gateado (teaser). */
  peek?: boolean;
  /** Descripción bajo el candado (overlay no-peek / wall). */
  description?: string;
  /** wall: bullets de lo que desbloquea el plan. */
  bullets?: string[];
  className?: string;
  children: React.ReactNode;
}

type GateProps = CommonProps & {
  product: "360";
  feature: PlanFeature;
  tour?: GateResource | null;
};

/** Card de upsell completa — overlay (no-peek) y wall. */
function LockCard({
  product,
  requiredPlan,
  description,
  bullets,
}: {
  product: GateProduct;
  requiredPlan: string;
  description?: string;
  bullets?: string[];
}) {
  const { t } = useTranslation();
  void product;
  return (
    <div className="flex flex-col items-center gap-3 text-center px-6">
      {bullets?.length ? (
        <p className="font-anton text-2xl text-foreground">
          PLAN {requiredPlan.toUpperCase()}+
        </p>
      ) : (
        <>
          <div className="flex items-center justify-center size-12 rounded-full bg-primary/10">
            <Lock size={22} className="text-primary" />
          </div>
          <p className="text-sm text-foreground">
            {t("demo_locked_plan", { plan: requiredPlan })}
          </p>
        </>
      )}
      {description && (
        <p className="text-xs text-muted-foreground max-w-xs">{description}</p>
      )}
      {bullets?.length ? (
        <ul className="flex flex-col items-start gap-2 text-sm text-muted-foreground">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Check size={10} className="text-primary" />
              </div>
              {b}
            </li>
          ))}
        </ul>
      ) : null}
      <Button
        to={buildRoute.billing()}
        icon={CreditCard}
        label={
          bullets?.length
            ? t("upgrade_to_plan", { plan: requiredPlan })
            : t("upgrade_plan")
        }
        className="mt-1"
      />
    </div>
  );
}

/** Chip compacto — peek (overlay) y badge. Exportado para superficies genéricas
 *  (FormModal) que reciben el gate ya resuelto como boolean y solo necesitan
 *  el candado canónico en lugar de ocultar el control. */
export function LockChip({ requiredPlan }: { requiredPlan: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={buildRoute.billing()}
      className="inline-flex items-center gap-1.5 rounded-xl glass-card border border-primary/20 px-3 py-1.5 shadow-lg hover:border-primary/40 transition-colors"
    >
      <Lock size={13} className="text-primary shrink-0" />
      <span className="text-xs font-mono text-primary hover:underline">
        {t("plan_named", { plan: requiredPlan })}
      </span>
    </Link>
  );
}

export function FeatureGate(props: GateProps) {
  const {
    variant = "overlay",
    peek = false,
    description,
    bullets,
    className,
    children,
  } = props;
  const { allowed, product, requiredPlan } = useFeatureGate(
    { product: props.product, feature: props.feature },
    props.tour,
  );
  const planLabel = planDisplayLabel(product, requiredPlan);

  if (allowed) return <>{children}</>;

  if (variant === "badge") return <LockChip requiredPlan={planLabel} />;

  if (variant === "wall") {
    return (
      <div
        className={cn(
          "surface-card p-8 md:p-12 flex flex-col items-center gap-5 text-center bg-primary/5 border border-primary/15",
          className,
        )}
      >
        <LockCard
          product={product}
          requiredPlan={planLabel}
          description={description}
          bullets={bullets}
        />
      </div>
    );
  }

  // overlay
  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "pointer-events-none select-none",
          peek ? "blur-sm opacity-35" : "opacity-50",
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          "absolute inset-0 z-50 flex flex-col items-center justify-center gap-4 rounded-2xl",
          !peek && "bg-background/80 backdrop-blur-sm",
        )}
      >
        {peek ? (
          <LockChip requiredPlan={planLabel} />
        ) : (
          <LockCard
            product={product}
            requiredPlan={planLabel}
            description={description}
          />
        )}
      </div>
    </div>
  );
}
